import {cloneDeep} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Avatar} from '@contentlab/api/avatars';
import {
  type CallToAction,
  type ScriptConfiguration,
  ScriptStyle,
} from '@contentlab/api/avatars/types';
import {DraftProjectStatus, ProjectType} from '@contentlab/api/project/types';
import FeatureEditor, {
  FullHeightEditorContainer,
} from '@contentlab/components/feature-editor';
import {creditsTooltipTextForFeature} from '@contentlab/components/feature-editor/utils';
import SimulatedProgressLoadingScreen from '@contentlab/components/simulated-progress-loading-screen';
import {
  DEFAULT_DEBOUNCE_DELAY,
  FEATURE_BEST_PRACTICES_LOCALE_KEY,
  PROJECT_ID_SEARCH_PARAM_KEY,
} from '@contentlab/config/constants';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import useManageDraftProject from '@contentlab/hooks/mutations/use-manage-draft-project/useManageDraftProject';
import useAvatarsQuery from '@contentlab/hooks/use-avatars-query';
import useGenerateAvatarScripts from '@contentlab/hooks/use-generate-avatar-scripts';
import useGenerateProject from '@contentlab/hooks/use-generate-project/useGenerateProject';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import useReadyAssets from '@contentlab/hooks/use-ready-assets';
import useVoicesQuery from '@contentlab/hooks/use-voices-query';
import contentLabStore, {
  contentLabActions,
  type DraftAiAvatarProject,
  type PreGenerateForm,
  ErrorKind,
  FileStatus,
  UploadedVideo,
} from '@contentlab/stores/contentLabStore';
import {UploadMode} from '@contentlab/types/UploadMode';
import {AvatarKind, ConfigMode} from '@contentlab/types/aiAvatars';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {raiseContentLabError} from '@contentlab/utils/contentLabErrorHandling';
import getDefaultProjectName from '@contentlab/utils/getDefaultProjectName';
import ttsCache from '@contentlab/utils/ttsPreviewCache';
import CustomizeScriptConfig from '@contentlab/views/contentlab/pages/ai-avatar-page/components/config-menu/script-config-menu';
import {DEFAULT_SCRIPT_CONFIG} from '@contentlab/views/contentlab/pages/ai-avatar-page/constants';
import {CUSTOM_GOAL_VALUE} from '@contentlab/views/contentlab/pages/ai-avatar-page/constants/goalOptions';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets';
import useDebouncedCallback from '@/hooks/use-debounce/useDebouncedCallback';
import AiAvatarEditor from './components/ai-avatar-editor';
import AiAvatarVideoDisplay from './components/ai-avatar-video-display';
import AvatarConfigMenu from './components/config-menu/avatar-config-menu/AvatarConfigMenu';
import EmptyAiAvatarContent from './components/empty-ai-avatar-content';
import PreGenerateScript from './components/pre-generate-script';
import {
  getEffectiveAvatarConfig,
  mapScriptDataToApiFormat,
} from './utils/aiAvatarDraftProjectUtils';

const DEFAULT_PROJECT_NAME = 'Avatar';

enum RightColumnState {
  EMPTY = 'empty',
  AVATAR_LAYOUT = 'config',
  PRE_GENERATE = 'pre-generate',
  CUSTOMIZE_SCRIPT = 'customize-script',
  INITIAL_GENERATE_SCRIPT = 'initial-generate-script',
  DEFAULT = 'default',
}

function AiAvatarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {draftAiAvatarProject, configMode} = contentLabStore(
    useShallow((state) => ({
      draftAiAvatarProject: state.draftAiAvatarProject,
      configMode: state.draftAiAvatarProject.configMode,
    }))
  );

  const activeConfig = useMemo(() => {
    if (configMode === ConfigMode.LIBRARY) {
      return draftAiAvatarProject.avatarSpec.configLibrary;
    }
    if (configMode === ConfigMode.CUSTOM) {
      return draftAiAvatarProject.avatarSpec.configCustom;
    }
    return null;
  }, [
    configMode,
    draftAiAvatarProject.avatarSpec.configLibrary,
    draftAiAvatarProject.avatarSpec.configCustom,
  ]);

  const {
    uploadedVideos,
    uploadingFiles,
    assetErrors: clientAssetErrors,
  } = useFeatureAssets(FeatureName.AI_AVATAR);

  const {assetErrors, invalidAssetIds, validUploadedVideos} =
    useMergedAssetErrors({
      serverAssetErrors:
        draftAiAvatarProject?.validationErrors?.userInput?.assets || [],
      clientAssetErrors,
      uploadedVideos,
    });

  const {data: voices} = useVoicesQuery();
  const {data: avatars} = useAvatarsQuery();
  const {uiInteraction} = useUiInteractionEvent();
  const [createDropzoneConfig, setCreateDropzoneConfig] = useState<
    (() => object) | null
  >(null);
  const [isWordCountOutOfRange, setIsWordCountOutOfRange] = useState(false);

  // Derive selectedAvatar from store data (automatically synced when project loads or avatar is selected)
  const selectedAvatar = useMemo(() => {
    if (
      draftAiAvatarProject.selectedAvatarKind === AvatarKind.LIBRARY &&
      draftAiAvatarProject.avatarSpec.libraryId &&
      avatars
    ) {
      return (
        avatars.find(
          (avatar) => avatar.id === draftAiAvatarProject.avatarSpec.libraryId
        ) || null
      );
    }
    return null;
  }, [
    draftAiAvatarProject.selectedAvatarKind,
    draftAiAvatarProject.avatarSpec.libraryId,
    avatars,
  ]);
  const [isScriptEdited, setIsScriptEdited] = useState(false);

  // Derive hasGeneratedScripts from draft project data
  const hasGeneratedScripts = useMemo(() => {
    return draftAiAvatarProject.scriptData.scripts.length > 0;
  }, [draftAiAvatarProject.scriptData.scripts]);

  const {
    createDraftProject,
    updateDraftProject,
    isLoading: isManagingProjectLoading,
  } = useManageDraftProject({
    type: ProjectType.AI_AVATAR,
    draftProject: draftAiAvatarProject,
    projectId: searchParams.get(PROJECT_ID_SEARCH_PARAM_KEY),
    onGetProjectErrorCallback: () => {
      searchParams.delete(PROJECT_ID_SEARCH_PARAM_KEY);
      setSearchParams(searchParams);
    },
  });

  const debouncedUpdateOrCreateProject = useDebouncedCallback(async () => {
    await updateOrCreateProject();
  }, DEFAULT_DEBOUNCE_DELAY);

  const handleAvatarSelect = (avatar: Avatar) => {
    // Note: Store update is handled by AiAvatarEditor component
    // selectedAvatar will be derived automatically from the store
    debouncedUpdateOrCreateProject();
    uiInteraction({
      item_name: 'avatar_selected',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
      flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
      interaction_details: JSON.stringify(avatar),
      asset_id: avatar.id,
    });
  };

  const processEvent = useProcessEvent();

  const generateScriptsMutation = useGenerateAvatarScripts({
    processEventHandlers: processEvent,
    onSuccess: (data, requestParams) => {
      const scripts = data.generatedScripts || [];

      if (scripts.length > 0) {
        // Map scripts from API format and override with request config
        const scriptsWithConfig = scripts.map((script, index) => {
          return {
            title: script.title || '',
            script: script.script || '',
            inputScriptConfiguration:
              requestParams.scriptConfigurations[index] ||
              DEFAULT_SCRIPT_CONFIG,
          };
        });

        // Check if this is a customize request (single script) or pre-generate (multiple scripts)
        const isCustomizeRequest =
          requestParams.scriptConfigurations.length === 1;

        // Merge scripts: for customize, append to existing; for pre-generate, use new scripts
        const newScripts = isCustomizeRequest
          ? draftAiAvatarProject.scriptData.scripts.concat(scriptsWithConfig[0])
          : scriptsWithConfig;

        // Helper function to safely clone array with fallback
        const safeCloneArray = <T,>(arr: T[]): T[] => {
          try {
            return Array.isArray(arr) ? cloneDeep(arr) : [];
          } catch (error) {
            console.error('Failed to clone array:', error);
            return Array.isArray(arr) ? [...arr] : [];
          }
        };

        // Determine selectedScriptIndex and scriptsSnapshot based on request type
        let selectedScriptIndex: number;
        let scriptsSnapshot: typeof draftAiAvatarProject.scriptsSnapshot;

        if (isCustomizeRequest) {
          // Customize: select the new script (last one) and append to existing snapshot
          selectedScriptIndex = newScripts.length - 1;
          const currentSnapshot = safeCloneArray(
            draftAiAvatarProject.scriptsSnapshot
          );
          let newScriptSnapshot: (typeof scriptsWithConfig)[0];
          try {
            newScriptSnapshot = cloneDeep(scriptsWithConfig[0]);
          } catch (error) {
            console.error('Failed to clone script snapshot:', error);
            newScriptSnapshot = {...scriptsWithConfig[0]};
          }
          scriptsSnapshot = currentSnapshot.concat(newScriptSnapshot);
        } else {
          // Pre-generate: select first script and replace snapshot
          selectedScriptIndex = 0;
          scriptsSnapshot = safeCloneArray(scriptsWithConfig);

          // Auto-select first avatar after pre-generating scripts
          if (
            avatars &&
            avatars.length > 0 &&
            !draftAiAvatarProject.avatarSpec.libraryId
          ) {
            const firstAvatar = avatars[0];
            contentLabActions.updateDraftAiAvatarProjectAvatarSpec({
              libraryId: firstAvatar.id,
            });
            contentLabActions.setDraftAiAvatarProjectSelectedType(
              AvatarKind.LIBRARY
            );
          }
        }

        contentLabActions.updateDraftAiAvatarProjectScriptData({
          scripts: newScripts,
          selectedScriptIndex,
        });
        contentLabActions.updateDraftAiAvatarProject({
          scriptsSnapshot,
        });
      }
    },
    onError: () => {
      // TODO: Show error message to user
      // console.error('Failed to generate scripts:', error);
    },
  });

  const generateScripts = useCallback(
    async (
      assetId: string,
      promoting: string,
      goal: CallToAction | typeof CUSTOM_GOAL_VALUE,
      customGoal?: string
    ) => {
      const callToAction =
        goal === CUSTOM_GOAL_VALUE && customGoal ? customGoal : goal;
      const product = promoting.trim() || null;

      const configs: ScriptConfiguration[] = [
        ScriptStyle.Professional,
        ScriptStyle.Review,
        ScriptStyle.Reaction,
      ].map((scriptStyle) => ({
        scriptStyle,
        product,
        callToAction,
        targetPlatform: null,
        targetAudience: null,
        tone: null,
        scriptType: null,
        specialRequests: null,
      }));

      await generateScriptsMutation.mutateAsync({
        assetId,
        scriptConfigurations: configs,
        source: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_PRE_GENERATE_SCRIPT,
      });
    },
    [generateScriptsMutation]
  );

  // New function for customize script generation (single script)
  const generateCustomizedScript = useCallback(
    async (assetId: string, scriptConfig: ScriptConfiguration) => {
      await generateScriptsMutation.mutateAsync({
        assetId,
        scriptConfigurations: [scriptConfig], // Single script with custom config
        source: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_SCRIPT,
      });
    },
    [generateScriptsMutation]
  );

  const handleScriptsGenerated = useCallback(
    async (params: {
      assetId: string;
      promoting: string;
      goal: CallToAction | typeof CUSTOM_GOAL_VALUE;
      customGoal?: string;
    }) => {
      try {
        setIsGeneratingInitialScript(true);
        await generateScripts(
          params.assetId,
          params.promoting,
          params.goal,
          params.customGoal
        );
        setIsGeneratingInitialScript(false);
      } catch (error) {
        setIsGeneratingInitialScript(false);
        raiseContentLabError({
          errorTitle: translate('Script generation failed'),
          errorMessage: translate(
            "We couldn't generate your script. Please try again."
          ),
          errorKind: ErrorKind.FAILURE,
        });
        // Re-throw so PreGenerateScript knows generation failed
        throw error;
      }
    },
    [generateScripts]
  );

  const getAiAvatarDraft = useCallback(
    (draft: DraftAiAvatarProject, assetIdOverride?: string) => {
      const assetId = assetIdOverride || draft.assetId || '';

      const avatarSpec =
        draft.avatarSpec || ({} as DraftAiAvatarProject['avatarSpec']);
      const defaultVoiceId =
        voices && voices.length > 0 ? voices[0].id : undefined;
      // Get effective config based on selected avatar kind
      const effectiveConfig = getEffectiveAvatarConfig(
        draft.selectedAvatarKind,
        avatarSpec
      );

      const mappedAvatarSpec = (() => {
        const selectedKind = draft.selectedAvatarKind ?? null;
        const customVoice =
          avatarSpec.voiceIdCustom ?? avatarSpec.voiceId ?? null;
        const voiceOnlyId = avatarSpec.voiceIdVoiceOnly ?? null;
        const {avatarAudioReductionFactor, backgroundAudioReductionFactor} =
          avatarSpec;

        if (selectedKind === AvatarKind.CUSTOM) {
          if (avatarSpec.imageAssetId && customVoice) {
            return {
              type: AvatarKind.CUSTOM,
              imageAssetId: avatarSpec.imageAssetId,
              voiceId: customVoice,
              config: effectiveConfig,
              avatarAudioReductionFactor,
              backgroundAudioReductionFactor,
            };
          }
          return {
            type: AvatarKind.VOICE_ONLY,
            voiceId: (voiceOnlyId ?? defaultVoiceId ?? '') as string,
            avatarAudioReductionFactor,
            backgroundAudioReductionFactor,
          };
        }

        if (selectedKind === AvatarKind.VOICE_ONLY) {
          return {
            type: AvatarKind.VOICE_ONLY,
            voiceId: (voiceOnlyId ?? defaultVoiceId ?? '') as string,
            avatarAudioReductionFactor,
            backgroundAudioReductionFactor,
          };
        }

        if (selectedKind === AvatarKind.LIBRARY) {
          return {
            type: AvatarKind.LIBRARY,
            libraryId: avatarSpec.libraryId || '',
            config: avatarSpec.configLibrary || effectiveConfig,
            avatarAudioReductionFactor,
            backgroundAudioReductionFactor,
          };
        }

        if (avatarSpec.imageAssetId && customVoice) {
          return {
            type: AvatarKind.CUSTOM,
            imageAssetId: avatarSpec.imageAssetId,
            voiceId: customVoice,
            config: avatarSpec.configCustom || effectiveConfig,
            avatarAudioReductionFactor,
            backgroundAudioReductionFactor,
          };
        }
        if (avatarSpec.libraryId) {
          return {
            type: AvatarKind.LIBRARY,
            libraryId: avatarSpec.libraryId,
            config: effectiveConfig,
            avatarAudioReductionFactor,
            backgroundAudioReductionFactor,
          };
        }
        return {
          type: AvatarKind.VOICE_ONLY,
          voiceId: (voiceOnlyId ?? defaultVoiceId ?? '') as string,
          avatarAudioReductionFactor,
          backgroundAudioReductionFactor,
        };
      })();

      return {
        assetId,
        scriptData: mapScriptDataToApiFormat(draft.scriptData),
        avatarSpec: mappedAvatarSpec,
      };
    },
    [voices]
  );

  const isAvatarSpecValid = useCallback(
    (draft: DraftAiAvatarProject) => {
      const spec = draft.avatarSpec;
      if (spec.libraryId) return true;
      const hasCustomVoice = (spec.voiceIdCustom || spec.voiceId) ?? null;
      const hasVoiceOnly = (spec.voiceIdVoiceOnly || spec.voiceId) ?? null;
      if (spec.imageAssetId && hasCustomVoice) return true;
      if (hasVoiceOnly) return true;
      if (voices && voices.length > 0) return true;
      return false;
    },
    [voices]
  );

  // removed unused createProject

  const updateProject = useCallback(
    async (
      status: DraftProjectStatus = DraftProjectStatus.UPDATE_DRAFT,
      asset?: Asset
    ) => {
      if (!draftAiAvatarProject.isInitialized) return;
      if (!isAvatarSpecValid(draftAiAvatarProject)) return;
      const payload = {
        status,
        projectName:
          draftAiAvatarProject.projectName ||
          getDefaultProjectName(DEFAULT_PROJECT_NAME),
        type: ProjectType.AI_AVATAR,
        draft: getAiAvatarDraft(draftAiAvatarProject, asset?.id),
      };
      await updateDraftProject(payload);
      if (asset?.id) {
        contentLabActions.updateDraftAiAvatarProject({assetId: asset.id});
      }
    },
    [
      draftAiAvatarProject,
      isAvatarSpecValid,
      updateDraftProject,
      getAiAvatarDraft,
    ]
  );

  const updateOrCreateProject = async (asset?: Asset) => {
    const currentDraft = contentLabStore.getState().draftAiAvatarProject;
    if (!currentDraft.isInitialized) {
      if (!isAvatarSpecValid(currentDraft)) return;
      const payload = {
        projectName:
          currentDraft.projectName ||
          getDefaultProjectName(DEFAULT_PROJECT_NAME),
        type: ProjectType.AI_AVATAR,
        draft: getAiAvatarDraft(currentDraft, asset?.id),
      };
      await createDraftProject(payload);
      if (asset?.id) {
        contentLabActions.updateDraftAiAvatarProject({assetId: asset.id});
      }
    } else {
      if (!isAvatarSpecValid(currentDraft)) return;
      const payload = {
        status: DraftProjectStatus.UPDATE_DRAFT,
        projectName:
          currentDraft.projectName ||
          getDefaultProjectName(DEFAULT_PROJECT_NAME),
        type: ProjectType.AI_AVATAR,
        draft: getAiAvatarDraft(currentDraft, asset?.id),
      };
      await updateDraftProject(payload);
      if (asset?.id) {
        contentLabActions.updateDraftAiAvatarProject({assetId: asset.id});
      }
    }
  };

  const selectedAssetId = (() => {
    if (uploadedVideos.length === 1) {
      return uploadedVideos[0].id;
    }
    if (draftAiAvatarProject) {
      return draftAiAvatarProject.assetId;
    }
    return null;
  })();

  const readyAssets = useReadyAssets({
    assetIds: selectedAssetId ? [selectedAssetId] : [],
    onReady: (assets) => updateOrCreateProject(assets[0]),
    keepPreviousData: false,
  });

  const [readyAsset] = readyAssets;

  useEffect(() => {
    const [uploadedVideo] = uploadedVideos;
    // immediately clear assets if there is an issue
    // the assetErrors are cleared only after selection of another file
    if (invalidAssetIds.has(uploadedVideo?.id)) {
      contentLabActions.removeFeatureUploadedVideos(FeatureName.AI_AVATAR);
      contentLabActions.removeFeatureUploadingFiles(FeatureName.AI_AVATAR);
    }
  }, [uploadedVideos, invalidAssetIds]);

  useEffect(() => {
    // video uploaded in the onboarding popup => need to assign it's assetId
    if (uploadedVideos.length && !draftAiAvatarProject.assetId) {
      contentLabActions.setDraftAiAvatarProject({
        ...draftAiAvatarProject,
        assetId: uploadedVideos[0].id,
      });
    }
  }, []);

  // Auto-generation effect removed - user will manually trigger script generation

  // Clear generated TTS previews when leaving the AI Avatar page
  useEffect(() => {
    return () => {
      ttsCache.clear();
    };
  }, []);

  const handleAudioMixChange = useCallback(
    (
      newAvatarAudioReductionFactor: number,
      newBackgroundAudioReductionFactor: number
    ) => {
      contentLabActions.updateDraftAiAvatarProjectAvatarSpec({
        avatarAudioReductionFactor: newAvatarAudioReductionFactor,
        backgroundAudioReductionFactor: newBackgroundAudioReductionFactor,
      });
      debouncedUpdateOrCreateProject();
    },
    [debouncedUpdateOrCreateProject]
  );

  const renderUploadContent = ([video]: UploadedVideo[]) => (
    <AiAvatarVideoDisplay
      video={video}
      asset={readyAsset}
      uploadingFiles={uploadingFiles}
      createDropzoneConfig={createDropzoneConfig}
      selectedAvatar={selectedAvatar}
      projectId={draftAiAvatarProject.projectId!}
      isScriptEdited={isScriptEdited}
      handleAudioMixChange={handleAudioMixChange}
      showAudioMixer={hasGeneratedScripts}
    />
  );

  const onFilePickerReady = useCallback(
    (dropzoneConfigFactory: () => object) => {
      setCreateDropzoneConfig(() => dropzoneConfigFactory);
    },
    [setCreateDropzoneConfig]
  );

  const onFilesSelected = useCallback(() => {
    // clear all current asset errors upon file selection
    contentLabActions.removeFeatureAssetErrors(FeatureName.AI_AVATAR);
  }, []);

  const getRequiredCredits = () => {
    if (uploadedVideos.length === 0) {
      return 0;
    }
    return calculateVideoTokensForAssets(uploadedVideos);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingInitialScript, setIsGeneratingInitialScript] =
    useState<boolean>(false);
  const generateProject = useGenerateProject({
    getRequiredCredits,
    setIsSubmitting,
    draftProject: draftAiAvatarProject,
    defaultProjectName: DEFAULT_PROJECT_NAME,
    updateProject: async (status) => {
      await updateProject(status, readyAsset);
    },
    resetProject: () => {
      setIsScriptEdited(false);
      contentLabActions.removeFeatureUploadingFiles(FeatureName.AI_AVATAR);
      contentLabActions.removeFeatureUploadedVideos(FeatureName.AI_AVATAR);
      contentLabActions.resetDraftAiAvatarProject();
    },
  });

  const handleGenerateClick = async () => {
    setIsSubmitting(true);
    try {
      uiInteraction({
        item_name: 'generate_ai_avatar_button',
        interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
        flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
      });
      await generateProject();
    } catch {
      // ignore
    }
    setIsSubmitting(false);
  };

  const hasScript = (() => {
    const scripts = draftAiAvatarProject.scriptData.scripts || [];
    const idx = draftAiAvatarProject.scriptData.selectedScriptIndex;
    return (
      scripts.length > 0 &&
      idx !== null &&
      idx !== undefined &&
      idx >= 0 &&
      idx < scripts.length
    );
  })();

  const isGenerateButtonDisabled = useMemo(() => {
    return (
      isSubmitting ||
      (uploadedVideos.length === 0 && !draftAiAvatarProject.assetId) ||
      Object.values(uploadingFiles).some(
        (f) => f.status === FileStatus.IN_PROGRESS
      ) ||
      !draftAiAvatarProject.isInitialized ||
      readyAsset?.status !== 'ready' ||
      !hasScript ||
      !draftAiAvatarProject.avatarSpec ||
      isWordCountOutOfRange
    );
  }, [
    isSubmitting,
    uploadedVideos,
    uploadingFiles,
    draftAiAvatarProject.assetId,
    draftAiAvatarProject.isInitialized,
    readyAsset?.status,
    hasScript,
    draftAiAvatarProject.avatarSpec,
    isWordCountOutOfRange,
  ]);

  const isGenerateButtonLoading = useMemo(
    () => isSubmitting || uploadedVideos.some((asset) => asset.isUploading),
    [isSubmitting, uploadedVideos]
  );

  const getFeatureDetailsComponent = () => {
    return (
      <AiAvatarEditor
        testID="ai-avatar-editor"
        hasUploadedVideo={uploadedVideos.length > 0}
        selectedAvatar={selectedAvatar}
        onAvatarSelect={handleAvatarSelect}
        onDraftChanged={() => debouncedUpdateOrCreateProject()}
        isSaving={isManagingProjectLoading}
        onOpenCustomizeScript={() => {
          contentLabActions.setDraftIsCustomizeScriptOpen(true);
          uiInteraction({
            item_name: 'customize_script_button',
            interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
          });
        }}
        onWordCountOutOfRange={setIsWordCountOutOfRange}
      />
    );
  };

  const {isAvatarLayoutOpen, isCustomizeScriptOpen} = contentLabStore(
    useShallow((state) => ({
      isAvatarLayoutOpen: state.draftAiAvatarProject.isAvatarLayoutOpen,
      isCustomizeScriptOpen: state.draftAiAvatarProject.isCustomizeScriptOpen,
    }))
  );
  const hasUploadedVideo =
    uploadedVideos.length > 0 || !!draftAiAvatarProject.assetId;

  const generateProjectDescription = useMemo(() => {
    if (isGeneratingInitialScript) {
      return '';
    }
    if (isSubmitting) {
      return '';
    }
    const isUploading = uploadedVideos.some((video) => video.isUploading);
    if (!hasUploadedVideo && !isUploading) {
      return translate('Upload a video to start generating your AI avatar');
    }
    return '';
  }, [
    uploadedVideos,
    hasUploadedVideo,
    isGeneratingInitialScript,
    isSubmitting,
  ]);

  // Pre-generate form handler
  const handlePreGenerateFormChange = useCallback(
    (updates: Partial<PreGenerateForm>) => {
      contentLabActions.updatePreGenerateForm(updates);
    },
    []
  );

  const getRightColumnOverride = () => {
    const getRightColumnContent = (): RightColumnState => {
      if (!hasUploadedVideo) {
        return RightColumnState.EMPTY;
      }
      if (isGeneratingInitialScript) {
        return RightColumnState.INITIAL_GENERATE_SCRIPT;
      }
      if (isCustomizeScriptOpen) {
        return RightColumnState.CUSTOMIZE_SCRIPT;
      }
      if (isAvatarLayoutOpen) {
        return RightColumnState.AVATAR_LAYOUT;
      }
      if (!hasGeneratedScripts) {
        return RightColumnState.PRE_GENERATE;
      }
      return RightColumnState.DEFAULT;
    };

    switch (getRightColumnContent()) {
      case RightColumnState.EMPTY:
        return <EmptyAiAvatarContent />;
      case RightColumnState.INITIAL_GENERATE_SCRIPT:
        return (
          <FullHeightEditorContainer>
            <SimulatedProgressLoadingScreen
              titleText={[
                translate('Analyzing your video'),
                translate('Finding scripts ideas'),
                translate('Structuring your narrative'),
                translate('Optimizing for your goal'),
                translate('Finalizing content'),
              ]}
              subtitleText={translate('This usually takes about 30 seconds')}
              footerText={translate(
                'Once your script is ready, you can use it as-is or edit/regenerate it'
              )}
            />
          </FullHeightEditorContainer>
        );
      case RightColumnState.AVATAR_LAYOUT:
        if (activeConfig) {
          return (
            <AvatarConfigMenu
              testID="avatar-config-menu"
              onClose={() =>
                contentLabActions.setDraftIsAvatarLayoutOpen(false)
              }
            />
          );
        }
        return null;
      case RightColumnState.CUSTOMIZE_SCRIPT: {
        const selectedScriptIndex =
          draftAiAvatarProject.scriptData.selectedScriptIndex ?? 0;
        const selectedScript =
          draftAiAvatarProject.scriptData.scripts[selectedScriptIndex];
        const scriptConfig = {
          ...(selectedScript?.inputScriptConfiguration ?? {
            ...DEFAULT_SCRIPT_CONFIG,
          }),
          scriptStyle: ScriptStyle.Neutral,
        };

        return (
          <CustomizeScriptConfig
            testID="customize-script-config"
            onClose={() => {
              setIsScriptEdited(false);
              contentLabActions.setDraftIsCustomizeScriptOpen(false);
            }}
            scriptConfig={scriptConfig}
            onScriptConfigChange={(config) =>
              contentLabActions.updateScriptConfiguration(
                selectedScriptIndex,
                config
              )
            }
            onConfigDirty={setIsScriptEdited}
            onApply={async (localConfig) => {
              if (localConfig && draftAiAvatarProject.assetId) {
                try {
                  await generateCustomizedScript(
                    draftAiAvatarProject.assetId,
                    localConfig
                  );
                  setIsScriptEdited(false);
                  contentLabActions.setDraftIsCustomizeScriptOpen(false);
                } catch {
                  raiseContentLabError({
                    errorTitle: translate('Script generation failed'),
                    errorMessage: translate(
                      "We couldn't generate your script. Please try again."
                    ),
                    errorKind: ErrorKind.FAILURE,
                  });
                }
              }
            }}
          />
        );
      }
      case RightColumnState.PRE_GENERATE:
        return (
          <PreGenerateScript
            testID="pre-generate-script"
            assetId={draftAiAvatarProject.assetId}
            formState={draftAiAvatarProject.preGenerateForm}
            onFormChange={handlePreGenerateFormChange}
            onScriptsGenerated={handleScriptsGenerated}
          />
        );
      case RightColumnState.DEFAULT:
      default:
        return undefined;
    }
  };

  return (
    <FeatureEditor
      feature={FeatureName.AI_AVATAR}
      featureDetailsProps={{
        featureDetailsComponent: getFeatureDetailsComponent(),
      }}
      renameProjectProps={{
        projectNamePlaceholder: translate('Name your Avatars project'),
        projectName: draftAiAvatarProject.projectName,
        projectNameChange: (name: string) => {
          contentLabActions.updateDraftAiAvatarProject({projectName: name});
        },
        onBlur: async (name: string) => {
          contentLabActions.updateDraftAiAvatarProject({projectName: name});
          await updateProject(DraftProjectStatus.UPDATE_DRAFT);
          uiInteraction({
            item_name: 'rename_project',
            interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
            flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
            interaction_id: uuid(),
          });
        },
      }}
      generateProjectProps={{
        description: generateProjectDescription,
        assetErrors,
        readyAssets,
        onGenerateClick: handleGenerateClick,
        creditsCost: getRequiredCredits(),
        disabled: isGenerateButtonDisabled,
        isLoading: isGenerateButtonLoading,
        tooltipText: creditsTooltipTextForFeature(
          FeatureName.AI_AVATAR,
          validUploadedVideos.length,
          0
        ),
      }}
      fileUploaderProps={{
        title: translate('Upload a video'),
        guideTitle: translate('One video. Max 3 min and 1 GB.', {
          maxDuration: translate(
            getUploadLimits(FeatureName.AI_AVATAR).maxDurationDisplayKey
          ),
          maxSize: translate(
            getUploadLimits(FeatureName.AI_AVATAR).maxFileSizeDisplayKey
          ),
        }),
        guideDescription: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.AI_AVATAR]
        ),
        shortenGuideText: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.AI_AVATAR]
        ),
        assetErrors,
        renderUploadContent,
        allowMultiUpload: false,
        uploadMode: UploadMode.SINGLE,
        onFilePickerReady,
        onFilesSelected,
      }}
      rightColumnOverride={getRightColumnOverride()}
    />
  );
}

export default AiAvatarPage;
